import { createFileRoute } from "@tanstack/react-router";
import { Footer, Header, Page, PageContent } from "@/components/page.tsx";
import { formatCurrency } from "@/lib/utils.ts";
import { useShop } from "@/hooks/use-shop.ts";
import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useMuiModal } from "@/components/modal";
import { ErrorModal } from "@/lib/api";
import { useTranslation } from "react-i18next";
import { getCashAgentUrl } from "@/lib/runtime-config";

export const Route = createFileRoute("/terminal-test")({
  component: RouteComponent,
});

// Generate amounts from 230 to 5000 with 100 steps
const amounts = [
  ...Array.from(
    { length: Math.floor((5000 - 230) / 100) + 1 },
    (_, i) => 230 + i * 100,
  ).map((elem) => elem * 100),
  503000,
];

function RouteComponent() {
  const { data: shop } = useShop();
  const [selectedAmount, setSelectedAmount] = useState<number | null>(null);
  const [sessionId, setSessionId] = useState<string>("");
  const [result, setResult] = useState<string | null>(null);
  const { showModal } = useMuiModal();
  const { t } = useTranslation();

  const terminalTransaction = useMutation({
    mutationKey: ["terminal-transaction"],
    mutationFn: async ({
      amount,
      sessionId,
    }: {
      amount: number;
      sessionId: string;
    }) => {
      const response = await fetch(
        `${getCashAgentUrl()}/payment/terminal-transaction`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            amount_cents: amount,
            session_id: sessionId || undefined,
          }),
        },
      );
      if (!response.ok) {
        throw new Error(`HTTP error: ${response.status}`);
      }
      const reponseData = await response.json();
      if (reponseData.errorDetails) {
        showModal(
          <ErrorModal
            errorKey={"server-message-terminal-payment-error"}
            errorDetails={
              reponseData.errorDetails +
              " " +
              t(reponseData.terminalResponse?.response_code_description ?? "")
            }
          />,
        );
      }
      // if (reponseData.success) {
      //   showModal(
      //     <>
      //       <p>Siker</p>
      //     </>,
      //   );
      // }
      return reponseData;
    },
    onSuccess: (data) => {
      setResult(`Success: ${JSON.stringify(data)}`);
    },
    onError: (error) => {
      setResult(`Error: ${error.message}`);
    },
  });

  const handleAmountSelect = (amount: number) => {
    setSelectedAmount(amount);
    setResult(null);
  };

  const handleSubmit = () => {
    if (selectedAmount) {
      terminalTransaction.mutate({ amount: selectedAmount, sessionId });
    }
  };

  return (
    <Page className="bg-background" skipInactvityModal>
      <Header showUserDetails={false} />
      <PageContent>
        <div className="text-center my-8">
          <h1 className="text-6xl font-helvetica font-bold text-primary mb-8">
            Terminal Test
          </h1>
        </div>

        <div className="flex justify-center gap-16 mb-8">
          {selectedAmount && (
            <div className="flex flex-col items-center justify-center">
              <span className="text-4xl text-primary mb-4">
                Selected Amount
              </span>
              <span className="text-5xl font-helvetica font-bold text-primary">
                {formatCurrency(selectedAmount, shop?.currencyCode ?? "HUF")}
              </span>
            </div>
          )}
          <div className="flex flex-col items-center justify-center">
            <label htmlFor="session-id" className="text-4xl text-primary mb-4">
              Session ID
            </label>
            <input
              id="session-id"
              type="text"
              value={sessionId}
              onChange={(e) => setSessionId(e.target.value)}
              placeholder="Enter session ID"
              className="text-3xl p-4 rounded-lg border-2 border-primary text-primary text-center w-80"
            />
          </div>
        </div>

        <div className="flex justify-between items-start p-8 mb-8 bg-[#33c0cd] rounded-lg">
          <div className="flex-1">
            <p className="text-white text-2xl leading-10 mb-4">
              Select an amount to test terminal payment:
            </p>
            <ScrollArea className="h-[400px] w-full">
              <div className="grid grid-cols-4 gap-4 pr-4">
                {amounts.map((amount) => (
                  <button
                    key={amount}
                    onClick={() => handleAmountSelect(amount)}
                    className={`p-4 rounded-lg text-2xl font-bold transition-colors ${
                      selectedAmount === amount
                        ? "bg-white text-[#33c0cd]"
                        : "bg-[#2aa8b4] text-white hover:bg-[#259aa5]"
                    }`}
                  >
                    {formatCurrency(amount / 100, shop?.currencyCode ?? "HUF")}
                  </button>
                ))}
              </div>
            </ScrollArea>
          </div>
          <div className="w-[200px] h-[160px] ml-10">
            <img
              src="/pin-pad.svg"
              alt="PIN pad"
              className="w-full h-full object-contain"
            />
          </div>
        </div>

        <div className="flex justify-center gap-8 mb-8">
          <Button
            onClick={handleSubmit}
            disabled={!selectedAmount || terminalTransaction.isPending}
            className="text-3xl"
          >
            {terminalTransaction.isPending
              ? "Processing..."
              : "Start Terminal Payment"}
          </Button>
        </div>

        {terminalTransaction.isPending && (
          <div className="flex justify-center mb-8">
            <div className="w-24 h-24">
              <img
                src="/dual-ball.gif"
                alt="Loading"
                className="w-full h-full object-contain"
              />
            </div>
          </div>
        )}

        {result && (
          <div
            className={`p-6 rounded-lg mx-8 mb-8 ${
              result.startsWith("Success")
                ? "bg-green-100 text-green-800"
                : "bg-red-100 text-red-800"
            }`}
          >
            <p className="text-2xl font-mono break-all">{result}</p>
          </div>
        )}
      </PageContent>

      <Footer helpText="Select an amount from the grid and click the button to test the terminal payment endpoint." />
    </Page>
  );
}
